/**
 * useBackendTask Hook
 *
 * React hook for subscribing to backend task updates.
 * Provides real-time task status tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import { BackendTaskService } from '../services/BackendTaskService';
import type { ContentAgentTask } from '../types';

/**
 * Hook return type
 */
interface UseBackendTaskReturn {
  task: ContentAgentTask | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Hook for subscribing to backend task updates
 *
 * @param projectId - Project ID
 * @param taskId - Task ID to subscribe to
 * @returns Task state and utilities
 */
export function useBackendTask(
  projectId: string | null,
  taskId: string | null
): UseBackendTaskReturn {
  const [task, setTask] = useState<ContentAgentTask | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Refresh function to manually re-fetch
  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    // Reset state when IDs change
    setTask(null);
    setLoading(true);
    setError(null);

    // Early return if no IDs provided
    if (!projectId || !taskId) {
      setLoading(false);
      return;
    }

    const taskService = new BackendTaskService();

    // Subscribe to task updates
    const unsubscribe = taskService.subscribe(
      projectId,
      taskId,
      (updatedTask) => {
        setTask(updatedTask);
        setLoading(false);
        setError(null);
      },
      (subscribeError) => {
        setError(subscribeError);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [projectId, taskId, refreshKey]);

  return {
    task,
    loading,
    error,
    refresh,
  };
}

export default useBackendTask;
